import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  forcedLight: boolean;
  toggleTheme: () => void;
  setForcedLight: (forced: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  forcedLight: false,
  toggleTheme: () => {},
  setForcedLight: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem("theme") as Theme | null;
    if (stored === "dark" || stored === "light") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const [forcedLight, setForcedLight] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark" && !forcedLight) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme, forcedLight]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  return (
    <ThemeContext.Provider value={{ theme, forcedLight, toggleTheme, setForcedLight }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
